import { createSlice } from '@reduxjs/toolkit';
import { IState } from 'shared/interfaces/state.interface';
import { initialStore } from '../initial-store';

const workflowSlice = createSlice({
  name: 'workflow',
  initialState: initialStore.workflow,
  reducers: {
    // Get current workflow
    getCurrentWorkflow: (state, { payload }) => {
      state.isLoading = true;
      state.error = null;
    },
    setCurrentWorkflow: (state, { payload }) => {
      state.isLoading = false;
      state.data = payload.workflow;
      state.conversationId = payload.conversationId;
      state.error = null;
    },
    getCurrentWorkflowFail: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
    },

    // Start workflow execution
    startWorkflowExecution: (state, { payload }) => {
      state.isExecuting = true;
      state.executionError = null;
    },
    setWorkflowExecution: (state, { payload }) => {
      state.isExecuting = false;
      state.execution = payload.execution;
      state.currentNode = payload.currentNode;
      state.isExisting = payload.isExisting || false;
      state.executionError = null;
    },
    startWorkflowExecutionFail: (state, { payload }) => {
      state.isExecuting = false;
      state.executionError = payload;
    },

    // Submit answer
    submitAnswer: (state, { payload }) => {
      state.isExecuting = true;
      state.executionError = null;
    },
    setAnswerResponse: (state, { payload }) => {
      state.isExecuting = false;
      state.execution = payload.execution;
      state.currentNode = payload.nextNode;
      state.actionResult = payload.actionResult;
      state.isCompleted = payload.isCompleted;

      // Add to message history
      if (!state.messages) {
        state.messages = [];
      }

      // Add user's selection
      state.messages.push({
        type: 'user',
        content: payload.userSelection.label,
        timestamp: new Date().toISOString()
      });

      // Add action result if present
      if (payload.actionResult) {
        state.messages.push({
          type: 'action',
          content: payload.actionResult,
          actionType: payload.actionType,
          timestamp: new Date().toISOString()
        });
      }

      state.executionError = null;
    },
    submitAnswerFail: (state, { payload }) => {
      state.isExecuting = false;
      state.executionError = payload;
    },

    // Clear workflow state
    clearWorkflow: (state) => {
      state.data = null;
      state.conversationId = null;
      state.execution = null;
      state.currentNode = null;
      state.messages = [];
      state.actionResult = null;
      state.isCompleted = false;
      state.isExisting = false;
      state.error = null;
      state.executionError = null;
    }
  }
});

export const {
  getCurrentWorkflow,
  setCurrentWorkflow,
  getCurrentWorkflowFail,
  startWorkflowExecution,
  setWorkflowExecution,
  startWorkflowExecutionFail,
  submitAnswer,
  setAnswerResponse,
  submitAnswerFail,
  clearWorkflow
} = workflowSlice.actions;

export const { reducer } = workflowSlice;

// Selectors
export const getWorkflowSelector = (state: IState) =>
  state && state.workflow?.data;
export const getWorkflowLoadingSelector = (state: IState) =>
  state && state.workflow?.isLoading;
export const getWorkflowErrorSelector = (state: IState) =>
  state && state.workflow?.error;
export const getWorkflowConversationIdSelector = (state: IState) =>
  state && state.workflow?.conversationId;
export const getWorkflowExecutionSelector = (state: IState) =>
  state && state.workflow?.execution;
export const getWorkflowCurrentNodeSelector = (state: IState) =>
  state && state.workflow?.currentNode;
export const getWorkflowExecutingSelector = (state: IState) =>
  state && state.workflow?.isExecuting;
export const getWorkflowExecutionErrorSelector = (state: IState) =>
  state && state.workflow?.executionError;
export const getWorkflowActionResultSelector = (state: IState) =>
  state && state.workflow?.actionResult;
export const getWorkflowMessagesSelector = (state: IState) =>
  state && state.workflow?.messages;
export const getWorkflowIsExistingSelector = (state: IState) =>
  state && state.workflow?.isExisting;
